import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../img/napvirag_logo1.png';
import { useAktualitasokEnabled } from '../hooks/useAktualitasokEnabled';
import { getDocumentUrl } from '../utils/documentUrl';
import { filterDocumentsForPublicMenu } from '../utils/documentDev';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({});
  const [navDocuments, setNavDocuments] = useState([]);
  const { enabled: aktualitasokEnabled } = useAktualitasokEnabled();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setMobileDropdownOpen({});
    if (location.pathname === '/') {
      scrollToTop();
    } else {
      navigate('/');
      setTimeout(scrollToTop, 100);
    }
  };

  useEffect(() => {
    fetch(`${API_BASE}/documents?placement=nav`)
      .then((res) => res.ok ? res.json() : [])
      .then((list) => {
        const docs = Array.isArray(list) ? list : [];
        setNavDocuments(filterDocumentsForPublicMenu(docs));
      })
      .catch(() => setNavDocuments([]));
  }, []);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen({});
  }, [location]);
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.nav-bottom-row') && !event.target.closest('.mobile-menu-toggle')) {
        setMobileMenuOpen(false);
        setMobileDropdownOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);
  
  const toggleMobileDropdown = (key) => {
    setMobileDropdownOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        {/* Logo - Left */}
        <div className="nav-logo">
          <Link to="/" onClick={handleLogoClick} aria-label="Vissza a főoldal tetejére">
            <img src={logoImage} alt="Napvirág" className="logo-image" />
          </Link>
        </div>
        
        {/* Menu Items - Center */}
        <div className="nav-bottom-row">
          <div className="mobile-menu-header">
            <Link to="/" onClick={handleLogoClick} aria-label="Vissza a főoldal tetejére">
              <img src={logoImage} alt="Napvirág" className="logo-image" />
            </Link>
          </div>
          <ul className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li className="nav-item">
              <Link 
                to="/" 
                className={isActive('/') ? 'active' : ''} 
                onClick={() => setMobileMenuOpen(false)}
              >
                Főoldal
              </Link>
            </li>
            <li className={`nav-item has-dropdown ${mobileDropdownOpen['info'] ? 'mobile-dropdown-open' : ''}`}>
              <Link 
                to="/informaciok" 
                className={isActive('/informaciok') ? 'active' : ''} 
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.preventDefault();
                    toggleMobileDropdown('info');
                  } else {
                    setMobileMenuOpen(false);
                  }
                }}
              >
                Információk
              </Link>
              <div className="dropdown-menu">
                {aktualitasokEnabled && (
                <Link 
                  to="/informaciok/bejelentesek" 
                  className={isActive('/informaciok/bejelentesek') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Aktualitások
                </Link>
                )}
                <Link 
                  to="/informaciok/magunkrol" 
                  className={isActive('/informaciok/magunkrol') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Magunkról
                </Link>
                <Link 
                  to="/informaciok/bekoltozes" 
                  className={isActive('/informaciok/bekoltozes') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  A beköltözésről
                </Link>
                <Link 
                  to="/informaciok/apartmanok" 
                  className={isActive('/informaciok/apartmanok') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Apartmanjaink
                </Link>
              </div>
            </li>
            <li className={`nav-item has-dropdown ${mobileDropdownOpen['events'] ? 'mobile-dropdown-open' : ''}`}>
              <Link 
                to="/esemenyek" 
                className={isActive('/esemenyek') ? 'active' : ''} 
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.preventDefault();
                    toggleMobileDropdown('events');
                  } else {
                    setMobileMenuOpen(false);
                  }
                }}
              >
                Események
              </Link>
              <div className="dropdown-menu">
                <Link 
                  to="/naptar" 
                  className={isActive('/naptar') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Naptár
                </Link>
                <Link 
                  to="/heti-etlap" 
                  className={isActive('/heti-etlap') ? 'active' : ''} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Heti Étlap
                </Link>
              </div>
            </li>
            <li className={`nav-item has-dropdown ${mobileDropdownOpen['docs'] ? 'mobile-dropdown-open' : ''}`}>
              <Link
                to="/dokumentumok"
                className={isActive('/dokumentumok') ? 'active' : ''}
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    toggleMobileDropdown('docs');
                  } else {
                    setMobileMenuOpen(false);
                  }
                }}
              >
                Dokumentumok
              </Link>
              <div className="dropdown-menu">
                {navDocuments.length > 0 ? (
                  navDocuments.map((doc) => (
                    doc._id ? (
                      <Link
                        key={doc._id}
                        to={`/dokumentumok/${doc._id}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {doc.name}
                      </Link>
                    ) : (
                      <a
                        key={doc.name}
                        href={getDocumentUrl(doc)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {doc.name}
                      </a>
                    )
                  ))
                ) : (
                  <span style={{ display: 'block', padding: '0.75rem 1rem', color: 'rgba(255,255,255,0.75)' }}>
                    Nincs elérhető dokumentum
                  </span>
                )}
              </div>
            </li>
            <li className="nav-item">
              <Link 
                to="/galeria" 
                className={isActive('/galeria') ? 'active' : ''} 
                onClick={() => setMobileMenuOpen(false)}
              >
                Galéria
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/virtualis-seta"
                className={isActive('/virtualis-seta') ? 'active' : ''}
                onClick={() => setMobileMenuOpen(false)}
              >
                Virtuális Séta
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                to="/elerhetosegek" 
                className={isActive('/elerhetosegek') ? 'active' : ''} 
                onClick={() => setMobileMenuOpen(false)}
              >
                Elérhetőségek
              </Link>
            </li>
          </ul>
        </div>
        
        {/* Mobile Menu Toggle */}
        <div className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
